import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Snackbar,
    Alert,
    Typography,
    IconButton,
    Grid,
    useTheme,
    useMediaQuery,
    Divider,
    Stack,
    Chip,
    LinearProgress,
} from '@mui/material';
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import EmojiNatureOutlinedIcon from '@mui/icons-material/EmojiNatureOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import { trackCTA } from '../../service/usercta';
import { createGratitudeJournal, getGratitudeJournals, updateGratitudeJournal, deleteGratitudeJournal } from "../../service/gratitudeJournal";

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3, 2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4, 4),
    },
    maxWidth: 1800,
    margin: '0 auto',
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #f6f8ff 0%, #fafffe 50%, #f8f6ff 100%)',
}));

const SectionCard = styled(Card)(({ theme }) => ({
    borderRadius: 20,
    border: '1px solid rgba(0,0,0,0.055)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.03)',
    background: '#ffffff',
    overflow: 'hidden',
}));

const InputCard = styled(SectionCard)(({ theme }) => ({
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: 'linear-gradient(90deg, #22c55e 0%, #14b8a6 50%, #8b5cf6 100%)',
        borderRadius: '20px 20px 0 0',
    },
}));

const StatCard = styled(Card)(() => ({
    borderRadius: 16,
    background: 'linear-gradient(135deg, #22c55e 0%, #14b8a6 55%, #8b5cf6 100%)',
    boxShadow: '0 4px 20px rgba(34,197,94,0.22)',
    border: 'none',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 28px rgba(34,197,94,0.28)',
    },
}));

const EntryRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 2.5),
    borderRadius: 14,
    border: '1px solid rgba(0,0,0,0.06)',
    background: '#fafafa',
    transition: 'background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
        background: '#ffffff',
        borderColor: 'rgba(34,197,94,0.18)',
        boxShadow: '0 2px 10px rgba(34,197,94,0.08)',
    },
}));

const IconBadge = styled(Box)(() => ({
    borderRadius: 12,
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#fff',
}));

const DatePill = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '3px 10px',
    borderRadius: 20,
    background: '#f1f5f9',
    border: '1px solid #e8edf4',
}));

const FilterChip = styled(Chip)(({ active, activecolor }) => ({
    fontWeight: 700,
    fontSize: '0.78rem',
    borderRadius: 10,
    height: 32,
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    ...(active === 'true'
        ? {
            background: activecolor || 'linear-gradient(135deg, #22c55e, #16a34a)',
            color: '#fff',
            boxShadow: '0 3px 10px rgba(34,197,94,0.22)',
        }
        : {
            background: '#f1f5f9',
            color: '#64748b',
            '&:hover': {
                background: '#e2e8f0',
            },
        }),
}));

// ─── Icon Config ──────────────────────────────────────────────────────────────

const gratitudeIcons = [
    { icon: <EmojiObjectsOutlinedIcon sx={{ fontSize: 18 }} />, bg: 'linear-gradient(135deg, #22c55e, #16a34a)' },
    { icon: <FavoriteBorderOutlinedIcon sx={{ fontSize: 18 }} />, bg: 'linear-gradient(135deg, #ef4444, #f97316)' },
    { icon: <WbSunnyOutlinedIcon sx={{ fontSize: 18 }} />, bg: 'linear-gradient(135deg, #f59e0b, #facc15)' },
    { icon: <EmojiNatureOutlinedIcon sx={{ fontSize: 18 }} />, bg: 'linear-gradient(135deg, #10b981, #34d399)' },
    { icon: <StarBorderOutlinedIcon sx={{ fontSize: 18 }} />, bg: 'linear-gradient(135deg, #8b5cf6, #a855f7)' },
    { icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />, bg: 'linear-gradient(135deg, #3b82f6, #06b6d4)' },
    { icon: <VolunteerActivismOutlinedIcon sx={{ fontSize: 18 }} />, bg: 'linear-gradient(135deg, #ec4899, #f472b6)' },
    { icon: <CelebrationOutlinedIcon sx={{ fontSize: 18 }} />, bg: 'linear-gradient(135deg, #f97316, #fb923c)' },
    { icon: <SpaOutlinedIcon sx={{ fontSize: 18 }} />, bg: 'linear-gradient(135deg, #14b8a6, #2dd4bf)' },
    { icon: <EmojiEmotionsOutlinedIcon sx={{ fontSize: 18 }} />, bg: 'linear-gradient(135deg, #6366f1, #818cf8)' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const GratitudeJournal = () => {
    const [gratitude, setGratitude] = useState('');
    const [openSuccess, setOpenSuccess] = useState(false);
    const [entries, setEntries] = useState([]);
    const [openEdit, setOpenEdit] = useState(false);
    const [editJournal, setEditJournal] = useState('');
    const [selectedJournalId, setSelectedJournalId] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState('today');
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedJournal, setSelectedJournal] = useState(null);
    const [summary, setSummary] = useState({ total_journals: 0, today_journals: 0, this_week_journals: 0, days_streak: 0 });

    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const openMenu = (event, journal) => {
        setAnchorEl(event.currentTarget);
        setSelectedJournal(journal);
        setSelectedJournalId(journal.id);
    };
    const closeMenu = () => setAnchorEl(null);

    const handleChange = (e) => setGratitude(e.target.value);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!gratitude.trim()) return;
        const user = JSON.parse(localStorage.getItem('user'));
        trackCTA('gratitude_journal', user);
        const response = await createGratitudeJournal(user, gratitude);
        if (response) {
            setGratitude('');
            setOpenSuccess(true);
            fetchGratitudeJournals();
        }
    };

    useEffect(() => { fetchGratitudeJournals(); }, []);

    const fetchGratitudeJournals = async (filter = 'today') => {
        const user = JSON.parse(localStorage.getItem('user'));
        const response = await getGratitudeJournals(user.id, filter);
        if (response) {
            setEntries(response.data.data);
            setSummary({
                total_journals: response.data.summary.total_journals,
                today_journals: response.data.summary.today_journals,
                this_week_journals: response.data.summary.this_week_journals,
                days_streak: response.data.summary.days_streak,
            });
        }
    };

    const handleCloseSuccess = () => setOpenSuccess(false);

    const stats = [
        { value: summary.total_journals, label: 'Total Entries' },
        { value: summary.this_week_journals, label: 'This Week' },
        { value: summary.days_streak, label: 'Day Streak' },
    ];

    const filters = [
        { key: 'today', label: `Today`, count: summary.today_journals, activeColor: 'linear-gradient(135deg, #22c55e, #16a34a)' },
        { key: 'week', label: `This Week`, count: summary.this_week_journals, activeColor: 'linear-gradient(135deg, #14b8a6, #0d9488)' },
        { key: 'all', label: `Total`, count: summary.total_journals, activeColor: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
    ];

    return (
        <PageWrapper>
            {/* ── Header ──────────────────────────────────────────── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 3,
                    px: { xs: 0, sm: 0.5 },
                }}
            >
                <Box>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 800,
                            fontSize: { xs: '1.25rem', sm: '1.45rem' },
                            background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            letterSpacing: '-0.3px',
                            lineHeight: 1.2,
                        }}
                    >
                        Gratitude Journal
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', mt: 0.4, fontSize: '0.82rem', letterSpacing: '0.01em' }}
                    >
                        Cultivating thankfulness, one entry at a time
                    </Typography>
                </Box>

                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{
                        background: '#f0f4ff',
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        borderRadius: '50%',
                        color: '#0B2D6B',
                        flexShrink: 0,
                        '&:hover': { background: '#e0ebff' },
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            {/* ── Content Column ──────────────────────────────────── */}
            <Box sx={{ maxWidth: 860, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                {/* ── Stats Row ───────────────────────────────────── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: { xs: 1.5, sm: 2 } }}>
                    {stats.map(({ value, label }) => (
                        <StatCard key={label}>
                            <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.8, sm: 2.2 }, textAlign: 'center' }}>
                                <Typography
                                    variant="h4"
                                    sx={{ fontWeight: 800, color: '#fff', lineHeight: 1, fontSize: { xs: '1.6rem', sm: '2rem' } }}
                                >
                                    {value}
                                </Typography>
                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.07em',
                                        color: 'rgba(255,255,255,0.85)',
                                    }}
                                >
                                    {label}
                                </Typography>
                            </Box>
                        </StatCard>
                    ))}
                </Box>

                {/* ── Input Card ──────────────────────────────────── */}
                <InputCard elevation={0}>
                    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary', mb: 2 }}
                        >
                            What are you grateful for today?
                        </Typography>
                        <form onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="I'm grateful for… (e.g., a warm cup of coffee, a friend's message, the sound of rain)"
                                value={gratitude}
                                onChange={handleChange}
                                multiline
                                rows={3}
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        fontSize: '0.92rem',
                                        background: '#f8fafb',
                                        '& fieldset': { borderColor: '#e5eaf2' },
                                        '&:hover fieldset': { borderColor: '#22c55e' },
                                        '&.Mui-focused fieldset': { borderColor: '#22c55e', borderWidth: 1.5 },
                                    },
                                }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={<AddIcon />}
                                disableElevation
                                sx={{
                                    background: 'linear-gradient(135deg, #22c55e 0%, #14b8a6 60%, #8b5cf6 100%)',
                                    textTransform: 'none',
                                    borderRadius: 2.5,
                                    px: 3.5,
                                    py: 1.1,
                                    fontWeight: 700,
                                    fontSize: '0.88rem',
                                    letterSpacing: '0.01em',
                                    boxShadow: '0 4px 14px rgba(34,197,94,0.3)',
                                    '&:hover': {
                                        boxShadow: '0 6px 20px rgba(34,197,94,0.4)',
                                        opacity: 0.95,
                                    },
                                }}
                            >
                                Add to Journal
                            </Button>
                        </form>
                    </Box>
                </InputCard>

                {/* ── Entries Card ────────────────────────────────── */}
                <SectionCard elevation={0}>
                    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                        {/* Filter chips */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                            {filters.map(({ key, label, count, activeColor }) => (
                                <FilterChip
                                    key={key}
                                    clickable
                                    active={selectedFilter === key ? 'true' : 'false'}
                                    activecolor={activeColor}
                                    onClick={() => {
                                        setSelectedFilter(key);
                                        fetchGratitudeJournals(key);
                                    }}
                                    label={`${label} · ${count}`}
                                />
                            ))}
                        </Box>

                        {/* Entries list */}
                        {entries.length > 0 ? (
                            <Box
                                sx={{
                                    maxHeight: 360,
                                    overflowY: 'auto',
                                    pr: 0.5,
                                    '&::-webkit-scrollbar': { width: 5 },
                                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                                    '&::-webkit-scrollbar-thumb': { background: '#d1fae5', borderRadius: 10 },
                                    '&::-webkit-scrollbar-thumb:hover': { background: '#6ee7b7' },
                                }}
                            >
                                <Stack spacing={1.5}>
                                    {entries.map((entry, index) => {
                                        const iconData = gratitudeIcons[index % gratitudeIcons.length];
                                        return (
                                            <EntryRow key={entry.id}>
                                                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                                                    {/* Icon badge */}
                                                    <IconBadge sx={{ background: iconData.bg }}>
                                                        {iconData.icon}
                                                    </IconBadge>

                                                    {/* Content */}
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        {/* Date + menu row */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, mb: 0.75 }}>
                                                            <DatePill>
                                                                <CalendarTodayOutlinedIcon sx={{ fontSize: 11, color: '#94a3b8' }} />
                                                                <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                    {new Date(entry.created_at).toLocaleString('en-IN', {
                                                                        day: '2-digit',
                                                                        month: 'short',
                                                                        hour: '2-digit',
                                                                        minute: '2-digit',
                                                                        hour12: true,
                                                                        timeZone: 'UTC',
                                                                    })}
                                                                </Typography>
                                                            </DatePill>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => openMenu(e, entry)}
                                                                sx={{
                                                                    p: 0.4,
                                                                    color: '#94a3b8',
                                                                    borderRadius: 1.5,
                                                                    '&:hover': { background: '#f1f5f9', color: '#475569' },
                                                                }}
                                                            >
                                                                <MoreVertIcon sx={{ fontSize: 17 }} />
                                                            </IconButton>
                                                        </Box>

                                                        {/* Journal text + New chip */}
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '0.9rem',
                                                                        fontWeight: 500,
                                                                        color: '#1e293b',
                                                                        lineHeight: 1.5,
                                                                        whiteSpace: 'normal',
                                                                    }}
                                                                >
                                                                    {entry.journal}
                                                                </Typography>
                                                                {index === 0 && (
                                                                    <Chip
                                                                        label="New"
                                                                        size="small"
                                                                        sx={{
                                                                            height: 20,
                                                                            fontSize: '0.6rem',
                                                                            fontWeight: 800,
                                                                            borderRadius: 999,
                                                                            background: '#dcfce7',
                                                                            color: '#15803d',
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Stack>
                                            </EntryRow>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 6 }}>
                                <Typography sx={{ fontSize: '1.8rem', mb: 1.5 }}>🌱</Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.88rem' }}>
                                    No entries yet. Add your first gratitude note above!
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </SectionCard>
            </Box>

            {/* ── Snackbar ────────────────────────────────────────── */}
            <Snackbar
                open={openSuccess}
                autoHideDuration={3000}
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSuccess}
                    severity="success"
                    sx={{ width: '100%', borderRadius: 2.5, fontWeight: 600, fontSize: '0.88rem' }}
                >
                    Gratitude entry added!
                </Alert>
            </Snackbar>

            {/* ── Context Menu ────────────────────────────────────── */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={closeMenu}
                PaperProps={{
                    sx: {
                        borderRadius: 2.5,
                        boxShadow: '0 8px 28px rgba(0,0,0,0.1)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        minWidth: 140,
                    },
                }}
            >
                <MenuItem
                    onClick={() => {
                        if (!selectedJournal) return;
                        setEditJournal(selectedJournal.journal);
                        setOpenEdit(true);
                        closeMenu();
                    }}
                    sx={{ fontSize: '0.88rem', gap: 1, py: 1.2 }}
                >
                    <EditOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
                    Edit
                </MenuItem>
                <MenuItem
                    onClick={async () => {
                        const user = JSON.parse(localStorage.getItem('user'));
                        if (!selectedJournalId) return;
                        const response = await deleteGratitudeJournal(user.id, selectedJournalId);
                        if (response) {
                            setEntries(prev => prev.filter(item => item.id !== selectedJournalId));
                            await fetchGratitudeJournals(selectedFilter);
                            closeMenu();
                            setSelectedJournal(null);
                            setSelectedJournalId(null);
                        }
                    }}
                    sx={{ fontSize: '0.88rem', gap: 1, py: 1.2, color: 'error.main' }}
                >
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                    Delete
                </MenuItem>
            </Menu>

            {/* ── Edit Dialog ─────────────────────────────────────── */}
            <Dialog
                open={openEdit}
                onClose={() => setOpenEdit(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>
                    Edit Gratitude
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={editJournal}
                        onChange={(e) => setEditJournal(e.target.value)}
                        sx={{
                            mt: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2.5,
                                fontSize: '0.92rem',
                                '&.Mui-focused fieldset': { borderColor: '#22c55e' },
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={() => setOpenEdit(false)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={async () => {
                            const user = JSON.parse(localStorage.getItem('user'));
                            if (!selectedJournalId) return;
                            const response = await updateGratitudeJournal(user.id, selectedJournalId, editJournal);
                            if (response) {
                                setOpenEdit(false);
                                fetchGratitudeJournals(selectedFilter);
                            }
                        }}
                        sx={{
                            background: 'linear-gradient(135deg, #22c55e, #14b8a6)',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 3,
                            boxShadow: '0 3px 10px rgba(34,197,94,0.3)',
                            '&:hover': { opacity: 0.92 },
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </PageWrapper>
    );
};

export default GratitudeJournal;
